import { IChart, TChartData } from "Interfaces";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IFromToChartOutput, useFromToContext } from "Providers/FromToProvider";
import { useSelectedProject } from "Hooks";
import useProjectTimezone from "Hooks/useProjectTimezone";
import { useExternalSyncs } from "Hooks/useExternalSyncs";
import { Box, SxProps, Theme, Tooltip } from "@mui/material";
import {
  ApiChartTemplate,
  ApiChartType,
  ApiChartYAxisKey,
  ApiDimension,
  ApiMetric,
} from "@incendium/api";
import StyledRadar from "Components/StyledRadar/StyledRadar";
import AnalyticsGeoMap from "features/analytics/components/AnalyticsGeoMap";
import { useUpdateEffect } from "react-use";
import { IOnClickMap, TTableCustomCellRender } from "../types/types";
import useChartData from "../hooks/useChartData";
import AnalyticsDataTable from "./AnalyticsDataTable";
import AnalyticsDoughnutWrapper from "./AnalyticsDoughnutWrapper";
import {
  getFirstMetricFromY,
  removeFirstDimension,
  uniqueMetricsFromChart,
} from "../utils/utils";
import AnalyticsComposedBridge from "./AnalyticsComposedBridge";
import AnalyticsChartArea from "Components/AnalyticsChartArea";
import StyledScatterChart from "./StyledScatterChart";
import AnalyticsReportTreeMap from "./AnalyticsReportTreeMap";
import AnalyticsFunnel from "./AnalyticsFunnel";
import { formatUTMSyncType } from "features/utms/utils";
import { usesPublisherDataMetrics } from "../services/metricFormatter";
import { ApiSyncType } from "@incendium/api";
import moment from "moment-timezone";
import { dateFormat } from "cfg";
import { InfoOutlined } from "@mui/icons-material";

const syncTypeStrokeMap: Partial<Record<ApiSyncType, string>> = {
  [ApiSyncType.FACEBOOK_ADS]: "#0766ff",
  [ApiSyncType.GOOGLE_ADS]: "#e2392f",
  [ApiSyncType.BING_ADS]: "#0f8270",
};

interface IAnalyticsChartProps {
  chart: IChart;
  customDataFn?: (arr: TChartData[]) => TChartData[];
  overrideDate?: IFromToChartOutput;
  overrideComparisonDate?: IFromToChartOutput;
  onClick?: {
    [field: string]:
      | ((v: string, o?: string) => void | Promise<void>)
      | IOnClickMap[];
  };
  onDataChange?: (
    arr: TChartData[],
    comp?: TChartData[],
    meta?: { metric?: ApiMetric; totals?: TChartData },
  ) => void;
  tableSx?: SxProps<Theme> | undefined;
  tableCustomCellRender?: TTableCustomCellRender;
  tableCustomHeaderRender?: TTableCustomCellRender;
  tableHiddenCols?: (ApiMetric | ApiDimension)[];
  tableHeaderAlign?: Partial<
    Record<ApiDimension | ApiMetric | string, "left" | "center">
  >;
  tableNonSortableCols?: (ApiMetric | ApiDimension | string)[];
  tableUnpinnedCols?: (ApiMetric | ApiDimension | string)[];
  onVisibleRowsChange?: (rows: any[]) => void;
  forceNoComparison?: boolean;
  ignoreAttrModel?: boolean;
  funnelShowStageChange?: boolean;
  verticalReferenceLines?: Array<{
    x: string;
    label?: string | React.ReactElement;
    stroke?: string;
  }>;
}

function AnalyticsChart({
  chart,
  customDataFn,
  overrideDate,
  overrideComparisonDate,
  onClick,
  onDataChange,
  tableSx,
  tableCustomCellRender,
  tableCustomHeaderRender,
  tableHiddenCols,
  tableHeaderAlign,
  tableNonSortableCols,
  tableUnpinnedCols,
  onVisibleRowsChange,
  forceNoComparison,
  ignoreAttrModel,
  funnelShowStageChange,
  verticalReferenceLines: customVerticalReferenceLines,
}: IAnalyticsChartProps) {
  const [editedChartDisplayOptions, setEditedChartDisplayOptions] = useState(
    chart.displayOptions,
  );
  const { chartOutput, chartComparisonOutput: chartComparisonOutputProp } =
    useFromToContext();
  const { selectedProject } = useSelectedProject();
  const timezone = useProjectTimezone();
  const { externalSyncs } = useExternalSyncs();
  const lastDataChangeRef = useRef<{
    data: TChartData[];
    compData?: TChartData[];
    metric?: ApiMetric;
    totals?: TChartData;
  } | null>(null);

  const syncVerticalReferenceLines = useMemo(() => {
    const chartMetrics = uniqueMetricsFromChart(chart);
    const hasPublisherMetric = chartMetrics.some((m) => {
      const metric = m.includes("\\") ? m.split("\\").pop()! : m;
      return usesPublisherDataMetrics.includes(metric as ApiMetric);
    });
    if (!hasPublisherMetric) return [];
    return (externalSyncs || [])
      .filter((sync) =>
        [
          ApiSyncType.FACEBOOK_ADS,
          ApiSyncType.GOOGLE_ADS,
          ApiSyncType.BING_ADS,
        ].includes(sync.type as ApiSyncType),
      )
      .filter((sync) => sync.syncedAt)
      .map((sync) => ({
        x: moment.utc(sync.syncedAt!).tz(timezone).format(dateFormat),
        label: (
          <Tooltip
            arrow
            title={`${sync.type ? formatUTMSyncType(sync.type) : (sync.accountName ?? "Sync")} Last Sync Date`}
          >
            <InfoOutlined />
          </Tooltip>
        ),
        stroke: syncTypeStrokeMap[sync.type as keyof typeof syncTypeStrokeMap],
      }));
  }, [externalSyncs, chart, timezone]);
  const verticalReferenceLines = useMemo(
    () =>
      customVerticalReferenceLines && customVerticalReferenceLines.length > 0
        ? [...syncVerticalReferenceLines, ...customVerticalReferenceLines]
        : syncVerticalReferenceLines,
    [customVerticalReferenceLines, syncVerticalReferenceLines],
  );

  const chartComparisonOutput =
    overrideComparisonDate || chartComparisonOutputProp;

  const {
    chartData,
    comparisonChartData,
    dimensions,
    loading,
    error,
    totalsData,
    comparisonTotalsData: compTotalsData,
  } = useChartData(selectedProject, chart, overrideDate || chartOutput, {
    ignoreModel: ignoreAttrModel,
    comparisonFrom: !forceNoComparison
      ? chartComparisonOutput?.from
      : undefined,
    comparisonTo: !forceNoComparison ? chartComparisonOutput?.to : undefined,
  });

  useUpdateEffect(() => {
    setEditedChartDisplayOptions(chart.displayOptions);
  }, [chart.displayOptions]);

  useEffect(() => {
    if (!onDataChange) return;

    const currentData = chartData?.data || [];
    const currentCompData = comparisonChartData?.data;
    const metric = getFirstMetricFromY(chartData?.y) as ApiMetric | undefined;
    const currentTotals = totalsData;

    // Only call onDataChange if the data actually changed
    const lastData = lastDataChangeRef.current;
    if (
      lastData &&
      lastData.data === currentData &&
      lastData.compData === currentCompData &&
      lastData.metric === metric &&
      lastData.totals === currentTotals
    ) {
      return;
    }

    // Check if arrays have different lengths or content
    const dataChanged =
      !lastData ||
      lastData.data.length !== currentData.length ||
      lastData.data !== currentData ||
      (lastData.compData?.length || 0) !== (currentCompData?.length || 0) ||
      lastData.compData !== currentCompData ||
      lastData.metric !== metric ||
      lastData.totals !== currentTotals;

    if (dataChanged) {
      onDataChange(currentData, currentCompData, {
        metric,
        totals: currentTotals,
      });
      lastDataChangeRef.current = {
        data: currentData,
        compData: currentCompData,
        metric,
        totals: currentTotals,
      };
    }
  }, [
    chartData?.data,
    chartData?.y,
    onDataChange,
    comparisonChartData?.data,
    totalsData,
  ]);

  const tableData = useMemo(() => {
    if (!chartData?.data) {
      return [];
    }

    return customDataFn ? customDataFn(chartData.data) : chartData.data;
  }, [chartData?.data, customDataFn]);

  const comparisonTableData = useMemo(() => {
    if (!comparisonChartData?.data) {
      return undefined;
    }

    return customDataFn
      ? customDataFn(comparisonChartData.data)
      : comparisonChartData.data;
  }, [comparisonChartData?.data, customDataFn]);

  const comparisonTableChartData = useMemo(
    () =>
      comparisonChartData && comparisonTableData
        ? {
            ...comparisonChartData,
            data: comparisonTableData,
          }
        : undefined,
    [comparisonChartData, comparisonTableData],
  );

  const renderChart = useCallback(() => {
    if (!chartData) {
      return <></>;
    }

    if (chart.template === ApiChartTemplate.MAP) {
      return (
        <AnalyticsGeoMap
          data={chartData.data}
          metric={
            ((chartData.y?.[0] as ApiChartYAxisKey).fields ||
              [])[0] as ApiMetric
          }
        />
      );
    }

    switch (chart.type) {
      case ApiChartType.TABLE:
        return (
          <AnalyticsDataTable
            data={tableData}
            totals={totalsData}
            onClick={onClick}
            comparisonChartData={comparisonTableChartData}
            comparisonTotals={compTotalsData || undefined}
            comparison={!!chartComparisonOutput}
            pageSize={editedChartDisplayOptions?.rowsPerPage}
            colWidths={chart.displayOptions?.tableColWidths}
            pinAt={chart.displayOptions?.tablePinAt}
            disableMetricClick={chart.displayOptions?.disabledMetricClick}
            disableDimensionClick={chart.displayOptions?.disabledDimensionClick}
            chartAttributes={chart.attributes}
            sx={tableSx}
            tableCustomCellRender={tableCustomCellRender}
            tableCustomHeaderRender={tableCustomHeaderRender}
            tableHeaderAlign={tableHeaderAlign}
            tableGroupings={chart.displayOptions?.tableGroupings}
            hiddenCols={tableHiddenCols}
            nonSortableCols={tableNonSortableCols}
            tableUnpinnedCols={tableUnpinnedCols}
            onVisibleRowsChange={onVisibleRowsChange}
          />
        );
      case ApiChartType.PIE:
        return (
          <Box sx={{ height: "calc(100% - 5px)", width: "100%" }}>
            <AnalyticsDoughnutWrapper
              chartData={chartData}
              comparisonChartData={comparisonChartData || undefined}
              totals={totalsData}
              comparisonTotals={compTotalsData}
              dataKey={getFirstMetricFromY(chartData.y)}
              comparison={!!chartComparisonOutput}
              pieProps={{
                showTotalText: chart.displayOptions?.pieTotalText,
                totalFontSize: chart.displayOptions?.pieTotalFontSize,
              }}
            />
          </Box>
        );
      case ApiChartType.GRAPH:
        // snapshot graph uses 'name' as dimension, tmp fix, we remove other dimensions, but later
        // todo: when only using new analytics remove requirement of name property
        return (
          <AnalyticsComposedBridge
            customDataFn={customDataFn}
            chartData={chartData}
            displayOptions={chart.displayOptions}
            dimensions={dimensions}
            comparisonChartData={
              !!chartComparisonOutput && comparisonChartData
                ? comparisonChartData
                : undefined
            }
            chartTemplate={chart.template}
            verticalReferenceLines={verticalReferenceLines}
          />
        );

      case ApiChartType.BUBBLE:
        return (
          <Box sx={{ height: "calc(100% - 5px)" }}>
            <StyledScatterChart
              showLabels={chart.displayOptions?.showLabels || false}
              dimension={
                dimensions && dimensions.length > 0 ? dimensions[0] : ""
              }
              data={chartData.data}
              yAxisKeys={chartData.y}
              showTooltip
            />
          </Box>
        );
      case ApiChartType.RADAR:
        return (
          <StyledRadar
            data={removeFirstDimension(chartData.data, dimensions)}
            yAxisKeys={chartData.y}
          />
        );
      case ApiChartType.TREE_MAP:
        return (
          <AnalyticsReportTreeMap
            chartData={chartData}
            dimensions={dimensions}
          />
        );
      case ApiChartType.FUNNEL:
        return (
          <AnalyticsFunnel
            chartData={
              customDataFn ? customDataFn(chartData.data) : chartData.data
            }
            comparisonChartData={
              comparisonChartData?.data
                ? customDataFn
                  ? customDataFn(comparisonChartData?.data)
                  : comparisonChartData?.data
                : undefined
            }
            useLogFn={chart.displayOptions?.funnelUseLogFn}
            additionalMetrics={chart.displayOptions?.funnelAdditonalMetrics}
            chartAttributes={chart.attributes}
            metricNames={chart.displayOptions?.metricNames}
            showStageChangeFromPrevious={funnelShowStageChange}
            alwaysShowAdditionalMetrics={
              chart.displayOptions?.funnelAlwaysShowAdditionalMetrics
            }
          />
        );
      default:
        break;
    }
  }, [
    chartData,
    comparisonTableChartData,
    tableData,
    dimensions,
    chart.type,
    chart.template,
    verticalReferenceLines,
    chart.displayOptions,
    chart.attributes,
    customDataFn,
    onClick,
    editedChartDisplayOptions,
    totalsData,
    compTotalsData,
    chartComparisonOutput,
    tableSx,
    tableCustomCellRender,
    tableCustomHeaderRender,
    tableHeaderAlign,
    tableHiddenCols,
    tableNonSortableCols,
    tableUnpinnedCols,
    funnelShowStageChange,
  ]);

  return (
    <AnalyticsChartArea
      loading={loading && chartData === null}
      error={!!error}
      graphLayout={
        chart.type === ApiChartType.GRAPH || chart.type === ApiChartType.FUNNEL
      }
    >
      {renderChart()}
    </AnalyticsChartArea>
  );
}

export default memo(AnalyticsChart);
